import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/app/Primitives";
import { ResultBadge, SimBadge, inr } from "./ResultBadge";
import { AIReviewButton } from "./AIReviewSheet";

export function CreatorsPanel({ campaign, creators, canEdit, home, onChange }) {
  const [handles, setHandles] = useState({});
  const save = async () => {
    try { await api.patch(`/campaigns/${campaign.id}/plan`, { handles, change_note: "Creator handles updated" }); toast.success("Handles saved"); setHandles({}); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="creators-panel">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-2">Creator</th><th className="px-4 py-2">Handle</th><th className="px-4 py-2">KYS / KYC</th><th className="px-4 py-2">Payment readiness</th><th className="px-4 py-2">Deliverables</th><th className="px-4 py-2 text-right">Approved cost</th><th className="px-4 py-2">Order</th></tr>
          </thead>
          <tbody>
            {creators.map((c) => {
              const eligible = c.kys_status === "verified";
              return (
                <tr key={c.order_id} className="border-t border-slate-100" data-testid={`creator-row-${c.influencer_id}`}>
                  <td className="px-4 py-3"><div className="font-semibold text-slate-800">{c.name}</div><div className="text-xs text-slate-500">{c.agency ? `Agency · ${c.agency}` : "Direct creator"}</div></td>
                  <td className="px-4 py-3">{canEdit ? <Input className="h-8 w-40 font-mono text-xs" defaultValue={c.handle} onChange={(e) => setHandles((h) => ({ ...h, [c.influencer_id]: e.target.value }))} data-testid={`creator-handle-${c.influencer_id}`} /> : <span className="font-mono text-xs">{c.handle || "—"}</span>}</td>
                  <td className="px-4 py-3"><StatusPill status={c.kys_status} /></td>
                  <td className="px-4 py-3"><ResultBadge value={eligible ? "PASS" : "FAIL"} /> <span className="text-xs text-slate-500 ml-1">{eligible ? "eligible" : "blocked · KYS incomplete"}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{c.deliverables_actual} / {c.deliverables_expected}</td>
                  <td className="px-4 py-3 text-right font-mono">{inr(c.approved_cost)}</td>
                  <td className="px-4 py-3"><Link to={`${home}/orders/${c.order_id}`} className="text-[#0052FF] text-xs hover:underline">{c.order_number}</Link> <StatusPill status={c.order_status} className="ml-1" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between">
        <SimBadge label="KYS/KYC · DEMO PROVIDER" />
        {canEdit && Object.keys(handles).length > 0 && <Button onClick={save} className="bg-[#0052FF] hover:bg-[#0043d1]" data-testid="creator-handles-save">Save handles</Button>}
      </div>
    </div>
  );
}

export function DeliverablesPoEPanel({ deliverables, onAIReview }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="deliverables-poe-panel">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between"><span className="text-sm font-semibold text-slate-800">Actual content · Proof of Execution ({deliverables.length})</span><SimBadge label="ADFLUENCE AUTO FETCH · SIMULATED" /></div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2">Creator</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Live URL</th><th className="px-4 py-2">Handle</th><th className="px-4 py-2">Link</th><th className="px-4 py-2">ASCI</th><th className="px-4 py-2">Hashtags</th><th className="px-4 py-2">AI content</th><th className="px-4 py-2">Status</th></tr></thead>
        <tbody>
          {deliverables.map((d) => { const p = d.poe || {}; return (
            <tr key={d.id} className="border-t border-slate-100" data-testid="poe-row">
              <td className="px-4 py-2">{d.influencer_name}</td><td className="px-4 py-2">{d.service_type}</td>
              <td className="px-4 py-2 max-w-[200px] truncate"><a href={d.url} target="_blank" rel="noreferrer" className="text-[#0052FF] text-xs hover:underline">{d.url}</a></td>
              <td className="px-4 py-2 font-mono text-xs">{p.handle || "—"}</td>
              <td className="px-4 py-2"><ResultBadge value={p.link_accessible === false ? "NEEDS_REVIEW" : d.url ? "PASS" : "FAIL"} /></td>
              <td className="px-4 py-2"><ResultBadge value={p.asci_disclosure ? "PASS" : "NEEDS_REVIEW"} /></td>
              <td className="px-4 py-2 text-xs text-slate-500">{(p.hashtags || []).join(" ")}</td>
              <td className="px-4 py-2"><AIReviewButton deliverable={d} onOpen={onAIReview} /></td>
              <td className="px-4 py-2"><StatusPill status={d.status} /></td>
            </tr>); })}
          {deliverables.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-400">No content fetched yet — run Sync now above</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
